import { cn } from '@shared/lib/cn';

/*
 * Visitor access's look, taken 1:1 from the approved mocks (sizes, radii, shadows, type). Colours
 * are the app's tokens nearest the mock's, so the organisation's brand colour still applies.
 */

/** A raised card: white, 16px corners, the mock's two-layer soft shadow. */
export const CARD =
  'rounded-[16px] border-0 bg-card shadow-[0_1px_2px_rgba(28,43,35,0.06),0_8px_18px_-8px_rgba(28,43,35,0.16)]';

/** Overview's bigger card, as Home draws its widgets: 22px corners and a deeper shadow. */
export const WIDGET = 'rounded-[22px] bg-card shadow-md';

/** A section label above a card ("Your PINs", "Contacts"). */
export const LABEL = 'm-0 text-base font-bold leading-[normal] text-ink';

/** A form field's label ("Courier", "Expected between"). */
export const FIELD_LABEL = 'text-[13px] font-semibold leading-[normal] text-ink-secondary';

/** Help text under a field or list. */
export const HELP = 'm-0 text-xs leading-[normal] text-ink-muted';

/** A list row inside a card, with a hairline between rows. */
export const ROW = 'flex w-full items-center gap-3 px-3.5 py-3 text-left text-ink';
export const ROW_DIVIDER = 'border-b border-hairline last:border-b-0';
export const ROW_TITLE = 'text-[15px] font-semibold leading-[normal] text-ink';
export const ROW_LINE = 'text-[13px] leading-[normal] text-ink-secondary';

/** The tinted square behind a row or tile icon. */
export const ICON_CHIP = 'flex h-9 w-9 shrink-0 items-center justify-center rounded-[12px] bg-primary-50 text-primary-500';

/** Buttons, laid over the shared Button: 48px tall, 14px corners, 15px semibold. */
export const BUTTON_PRIMARY = 'min-h-12 rounded-[14px] bg-primary-500 px-4 text-[15px] font-semibold text-primary-ink hover:bg-primary-600';
export const BUTTON_SECONDARY = 'min-h-12 rounded-[14px] border border-hairline bg-card px-4 text-[15px] font-semibold text-ink shadow-none hover:bg-card';
export const BUTTON_DANGER = 'min-h-12 rounded-[14px] border border-hairline bg-card px-4 text-[15px] font-semibold text-danger shadow-none hover:bg-card';
/** A small bordered button inside a row ("Cancel", "Change"). */
export const BUTTON_SMALL = 'min-h-9 shrink-0 rounded-[10px] border border-hairline bg-card px-3 py-0 text-[13px] font-semibold text-ink shadow-none hover:bg-card';

/** A text input or text area. */
export const INPUT =
  'min-h-[46px] w-full rounded-[12px] border border-hairline bg-card px-3.5 text-[15px] text-ink placeholder:text-ink-muted focus:border-primary-500 focus:outline-none';

/** A small status tag on a row ("Today", "Group"); the brass one marks deliveries and regulars. */
export const BADGE = 'shrink-0 rounded-[8px] bg-primary-50 px-2 py-1 text-xs font-semibold text-primary-700';
export const BADGE_BRASS =
  'shrink-0 rounded-[8px] px-2 py-1 text-xs font-semibold bg-[color:color-mix(in_srgb,var(--accent-brass)_22%,var(--surface-card))] text-[color:color-mix(in_srgb,var(--accent-brass)_45%,var(--ink-primary))]';

/** "Regular" and "On site": a soft brass tint that follows the tenant's brass colour. */
export const BRASS_BADGE =
  'bg-[color:color-mix(in_srgb,var(--accent-brass)_22%,var(--surface-card))] text-[color:color-mix(in_srgb,var(--accent-brass)_45%,var(--ink-primary))]';

/** A pill choice (a courier, a visit day), tinted when chosen. */
export function chipClass(selected: boolean): string {
  return cn(
    'min-h-10 rounded-[20px] border px-3.5 text-sm font-semibold transition-colors',
    selected ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-hairline bg-card text-ink',
  );
}

/** A two- or three-way choice: a track holding one filled segment. */
export const SEGMENTS = 'grid gap-1 rounded-[14px] p-1';
export function segmentClass(selected: boolean): string {
  return cn(
    'min-h-[42px] rounded-[11px] text-sm font-semibold transition-colors',
    selected ? 'bg-primary-600 text-primary-ink' : 'bg-transparent text-ink',
  );
}

/** A quiet destructive action spanning the action bar under the main two ("Stop being a regular"). */
export const BUTTON_DANGER_TEXT = 'col-span-2 min-h-10 bg-transparent text-sm font-semibold text-danger shadow-none hover:bg-transparent';

/** Laid over the shared TextInputField / TextAreaField (with INPUT as its box): the mock's 13px label. */
export const FIELD = '[&>label]:text-[13px] [&>label]:leading-[normal] [&>p]:font-normal';
